import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Subject } from 'rxjs';

const helper = new JwtHelperService();

import { Container, User, Currency, Country } from '../models';
import { LocalStorageKeys } from '../constants/local-storage';
import { ContainersService } from '.';
import { PoliciesService } from './policies.service';

@Injectable()
export class SessionService {
  private containerListChanged: Subject<void> = new Subject<void>();

  constructor(
    private containersService: ContainersService,
    private policiesService: PoliciesService
  ) { }

  public setWorkingContainer(container: Container): void {
    localStorage.setItem(LocalStorageKeys.WORKING_CONTAINER, JSON.stringify(container));
    this.policiesService.init();
  }

  public getWorkingContainer(): Container {
    return <Container>JSON.parse(localStorage.getItem(LocalStorageKeys.WORKING_CONTAINER));
  }

  public setContainersList(containers: Container[]): void {
    localStorage.setItem(LocalStorageKeys.CONTAINERS_LIST, JSON.stringify(containers));
  }

  public getContainersList(): Container[] {
    return <Container[]>JSON.parse(localStorage.getItem(LocalStorageKeys.CONTAINERS_LIST));
  }

  public setContainerUpeId(upeId: number): void {
    localStorage.setItem(LocalStorageKeys.CONTAINER_UPE_ID, JSON.stringify(upeId));
  }

  public getContainerUpeId(): number {
    return <number>JSON.parse(localStorage.getItem(LocalStorageKeys.CONTAINER_UPE_ID));
  }

  public setContainerUpeCurrency(currency: Currency): void {
    localStorage.setItem(LocalStorageKeys.CONTAINER_UPE_CURRENCY, JSON.stringify(currency));
  }

  public getContainerUpeCurrency(): Currency {
    return JSON.parse(localStorage.getItem(LocalStorageKeys.CONTAINER_UPE_CURRENCY));
  }

  public setContainerUpeCurrencyScale(currencyScale: number): void {
    localStorage.setItem(LocalStorageKeys.CONTAINER_UPE_CURRENCY_SCALE, JSON.stringify(currencyScale));
  }

  public getContainerUpeCurrencyScale(): number {
    return JSON.parse(localStorage.getItem(LocalStorageKeys.CONTAINER_UPE_CURRENCY_SCALE));
  }

  public setUserDetails(user?: User): void {
    if (!user) {
      const decodedToken = helper.decodeToken(this.getAuthToken());
      user = {
        firstName: decodedToken.given_name || 'User',
        lastName: decodedToken.family_name || '',
        username: decodedToken['cognito:username']
      };

    }
    localStorage.setItem(LocalStorageKeys.USER, JSON.stringify(user));
  }

  public getUserDetails(): User {
    return <User>JSON.parse(localStorage.getItem(LocalStorageKeys.USER));
  }

  public setAuthResult(IdToken: any, AccessToken: any, refreshToken?: any): void {
    const decodedToken = helper.decodeToken(IdToken.jwtToken);
    const expiresAt = moment(decodedToken.exp);
    localStorage.setItem(LocalStorageKeys.ID_TOKEN, IdToken.jwtToken);
    localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, AccessToken.jwtToken);
    localStorage.setItem(LocalStorageKeys.EXPIRATION, JSON.stringify(expiresAt.valueOf()));
    if (refreshToken) {
      localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN, refreshToken.token);
    }
  }

  public getAuthToken(): string {
    return localStorage.getItem(LocalStorageKeys.ID_TOKEN);
  }

  public getAccessToken(): string {
    return localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
  }

  public sessionExists(): boolean {
    return !!localStorage.getItem(LocalStorageKeys.ID_TOKEN);
  }

  public getRefreshToken(): string {
    return localStorage.getItem('refreshToken');
  }

  public getAuthExpiration(): any {
    return localStorage.getItem(LocalStorageKeys.EXPIRATION);
  }

  public emptySession(): void {
    localStorage.clear();
    sessionStorage.clear();
    this.policiesService.init();
  }

  public setWorkspaceChoice(workspaceChoice: string): void {
    localStorage.setItem(LocalStorageKeys.WORKSPACE_CHOICE, workspaceChoice);
  }

  public getWorkspaceChoice(): string {
    return localStorage.getItem(LocalStorageKeys.WORKSPACE_CHOICE);
  }

  public setCountries(countries: Country[]): void {
    localStorage.setItem(LocalStorageKeys.COUNTRIES, JSON.stringify(countries));
  }

  public getCountries(): Country[] {
    return <Country[]>JSON.parse(localStorage.getItem(LocalStorageKeys.COUNTRIES));
  }

  public getUserId(): string {
    const decodedToken = helper.decodeToken(this.getAuthToken());
    return decodedToken.userId;
  }

  public getUserRole(): string {
    const decodedToken = helper.decodeToken(this.getAuthToken());
    return decodedToken.role;
  }

  public getUserEula(): string {
    const decodedToken = helper.decodeToken(this.getAuthToken()) || {};
    return decodedToken.licenseAgreement;
  }

  public set2faEnabled(): void {
    localStorage.setItem(LocalStorageKeys.MFA_ENABLED, 'true');
  }

  public getIs2faEnabled(): boolean {
    return localStorage.getItem(LocalStorageKeys.MFA_ENABLED) === 'true';
  }

  public updateContainerList(): void {
    this.containersService.list().subscribe(({ data: containers }) => {
      containers = containers.map((container) => {
        container.displayName = container.name + ' ' + container.taxYear;
        return container;
      });

      this.setContainersList(containers);
      this.containerListChanged.next();
    });
  }

  public onContainerListChangeSubscription(): Subject<void> {
    return this.containerListChanged;
  }

  public setUserIdForWalkMe(): void {
    const decodedToken = helper.decodeToken(this.getAuthToken());
    (window as any).userId = decodedToken && decodedToken.userId;
  }

  getRedirctFromLoginFlag(): string {
    return localStorage.getItem(LocalStorageKeys.REDIRECT_FLAG);
  }

  resetRedirctFromLoginFlag(): void {
    localStorage.setItem(LocalStorageKeys.REDIRECT_FLAG, 'false');
  }
}
